import React, { useLayoutEffect, useRef, useState } from 'react';

/** Скорость бегущей строки, px/с: читается на ходу, но не заставляет ждать. */
export const MARQUEE_SPEED = 36;
/** Пауза перед стартом и на каждом краю, мс: начало строки успевают прочесть. */
export const MARQUEE_PAUSE_MS = 900;
/** Короче этого хвост не прокручиваем: пара пикселей — не «не влезло». */
const MIN_SHIFT = 4;

/**
 * Одна строка текста, которая у АКТИВНОГО элемента прокручивается бегущей
 * строкой, если не влезла (плитка канала под курсором или в фокусе, название
 * передачи в столбце канала). У остальных — обычное многоточие.
 *
 * Дёшево: мерка — одна, при включении; движение — только `transform`, его
 * ведёт композитор (Web Animations), а не раскладка. Строка стоит в начале,
 * доезжает до конца, стоит, возвращается — и так по кругу. Края прокрутки
 * растворяются в поле элемента, как у всех лент (правило — в styles.css).
 * «Меньше движения» в системе — строка стоит с многоточием.
 */
export function Marquee({
  text,
  active,
  className,
}: {
  text: string;
  /** Элемент активен: под курсором, в фокусе или выбран. */
  active: boolean;
  className?: string;
}): React.JSX.Element {
  const host = useRef<HTMLSpanElement>(null);
  const track = useRef<HTMLSpanElement>(null);
  const [running, setRunning] = useState(false);

  useLayoutEffect(() => {
    const box = host.current;
    const line = track.current;
    if (!active || box === null || line === null || typeof line.animate !== 'function' || prefersStill()) {
      setRunning(false);
      return undefined;
    }
    let animation: Animation | null = null;
    const start = (): void => {
      animation?.cancel();
      animation = null;
      // Ширина строки целиком против видимой части — без полей, где край тает.
      const shift = line.offsetWidth - (box.clientWidth - fieldOf(box));
      if (shift < MIN_SHIFT) {
        setRunning(false);
        return;
      }
      setRunning(true);
      const travel = (shift / MARQUEE_SPEED) * 1000;
      const total = MARQUEE_PAUSE_MS + travel + MARQUEE_PAUSE_MS;
      const end = `translate3d(${-shift}px, 0, 0)`;
      animation = line.animate(
        [
          { transform: 'translate3d(0, 0, 0)', offset: 0 },
          { transform: 'translate3d(0, 0, 0)', offset: MARQUEE_PAUSE_MS / total },
          { transform: end, offset: (MARQUEE_PAUSE_MS + travel) / total },
          { transform: end, offset: 1 },
        ],
        { duration: total, iterations: Infinity, direction: 'alternate', easing: 'linear' },
      );
    };
    start();
    // Ширина меняется (окно, догрузившийся шрифт) — мерим заново.
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(start) : null;
    observer?.observe(box);
    return () => {
      observer?.disconnect();
      animation?.cancel();
    };
  }, [active, text]);

  const classes = ['marquee', running ? 'is-running' : '', className ?? ''].filter(Boolean).join(' ');
  return (
    <span ref={host} className={classes} title={text}>
      <span ref={track} className="marquee-track">{text}</span>
    </span>
  );
}

/** Поля элемента по бокам: в них край строки растворяется. */
function fieldOf(element: HTMLElement): number {
  const style = window.getComputedStyle(element);
  return (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0);
}

function prefersStill(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
